import {
  ApiRequestError,
  executeSdkEnvelopeRequest,
} from './api-request.util';
import { adminStreamingPlatformsApi } from './sdk-client';

export class AdminStreamingPlatformsError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'AdminStreamingPlatformsError');
  }
}

export type AdminStreamingPlatform = {
  id: string;
  code: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
};

export type CreateStreamingPlatformInput = {
  code: string;
  name: string;
  display_order: number;
  is_active?: boolean;
};

export type UpdateStreamingPlatformInput = {
  code?: string;
  name?: string;
  display_order?: number;
  is_active?: boolean;
};

type StreamingPlatformMutationResult = {
  message: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asBoolean(value: unknown, fallback = true): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function parseStreamingPlatform(value: unknown): AdminStreamingPlatform | null {
  if (!isRecord(value) || typeof value.id !== 'string') return null;
  return {
    id: value.id,
    code: asString(value.code),
    name: asString(value.name),
    displayOrder: asNumber(value.displayOrder, 0),
    isActive: asBoolean(value.isActive, true),
  };
}

function parseMessageResult(
  json: unknown,
  fallback: string,
): StreamingPlatformMutationResult {
  if (isRecord(json) && typeof json.message === 'string') {
    return { message: json.message };
  }
  return { message: fallback };
}

export async function listStreamingPlatforms(): Promise<
  AdminStreamingPlatform[]
> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminStreamingPlatformsApi.adminStreamingPlatformsControllerListV1Raw(),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      if (!Array.isArray(envelope.data)) return [];
      return envelope.data
        .map(parseStreamingPlatform)
        .filter((item): item is AdminStreamingPlatform => item != null);
    },
    fallback: 'Failed to load streaming platforms',
    ErrorClass: AdminStreamingPlatformsError,
  });
}

export async function createStreamingPlatform(
  input: CreateStreamingPlatformInput,
): Promise<AdminStreamingPlatform | StreamingPlatformMutationResult> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminStreamingPlatformsApi.adminStreamingPlatformsControllerCreateV1Raw({
        createStreamingPlatformDto: input,
      }),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      const mapped = parseStreamingPlatform(envelope.data);
      if (mapped) return mapped;
      return parseMessageResult(json, 'Streaming platform created');
    },
    fallback: 'Failed to create streaming platform',
    ErrorClass: AdminStreamingPlatformsError,
  });
}

export async function updateStreamingPlatform(
  id: string,
  input: UpdateStreamingPlatformInput,
): Promise<StreamingPlatformMutationResult> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminStreamingPlatformsApi.adminStreamingPlatformsControllerUpdateV1Raw({
        id,
        updateStreamingPlatformDto: input,
      }),
    parse: (json) => parseMessageResult(json, 'Streaming platform updated'),
    fallback: 'Failed to update streaming platform',
    ErrorClass: AdminStreamingPlatformsError,
  });
}

export async function deleteStreamingPlatform(
  id: string,
  force?: boolean,
): Promise<StreamingPlatformMutationResult> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminStreamingPlatformsApi.adminStreamingPlatformsControllerDeleteV1Raw({
        id,
        force,
      }),
    parse: (json) => parseMessageResult(json, 'Streaming platform deleted'),
    fallback: 'Failed to delete streaming platform',
    ErrorClass: AdminStreamingPlatformsError,
  });
}
